import {useLiveCaptionStore, useUserStore} from '@/stores'
import {jsonRpc} from '@communication'
import {startLiveCaption, stopLiveCaption} from '@/messages/liveCaptionMessage'
import {useStreamStore} from '@/stores/streamStore'
import {useTranslation} from 'i18next-vue'
import {useEventBus} from '@vueuse/core'
import {onMounted, onUnmounted, watch} from 'vue'
import {useMessageStore} from '@/stores/messagesStore'

interface AudioProcessor {
  context: AudioContext
  worklet: AudioWorkletNode
  source: MediaStreamAudioSourceNode
}

interface TranscriptEvent {
  party: string
  transcript: string
  isPartial: boolean
  resultId: string
  partyId: string
  languageCode: string
}

const AUDIO_SEND_CHUNK_BYTES = 6400

// payload: TextTrack
export const useLiveCaptionCueChangedBus = () => useEventBus<TextTrack>('liveCaption:cueChanged')

export const useLiveCaption = () => {
  const start = async () => {
    await jsonRpc.sendAndWait(startLiveCaption())
  }
  const stop = async () => {
    await jsonRpc.sendAndWait(stopLiveCaption())
  }
  return {start, stop}
}

export const useLiveCaptionCallback = () => {
  const {t} = useTranslation()
  const user = useUserStore()
  const liveCaptionStore = useLiveCaptionStore()
  const cueChangedBus = useLiveCaptionCueChangedBus()

  let audioProcessor: AudioProcessor | undefined
  // Bumped on every close so a processor still being created knows it was stopped or superseded
  let audioProcessorVersion = 0
  // True between stt:start and stt:stop, i.e. while the microphone should be streamed
  let isLiveCaptionOn = false
  const activeCues = new Map<string, Map<string, VTTCue>>()
  let audioSendBufferBytes = 0
  let audioSendBuffer: ArrayBuffer[] = []

  const flushAudioSendBuffer = () => {
    if (audioSendBufferBytes === 0) {
      return
    }
    const merged = new Uint8Array(audioSendBufferBytes)
    let offset = 0
    for (const buffer of audioSendBuffer) {
      merged.set(new Uint8Array(buffer), offset)
      offset += buffer.byteLength
    }
    audioSendBuffer = []
    audioSendBufferBytes = 0
    jsonRpc.sendBinary(merged.buffer)
  }

  const enqueueAudioChunk = (buffer: ArrayBuffer) => {
    audioSendBuffer.push(buffer)
    audioSendBufferBytes += buffer.byteLength
    if (audioSendBufferBytes >= AUDIO_SEND_CHUNK_BYTES) {
      flushAudioSendBuffer()
    }
  }

  const closeAudioProcessor = async () => {
    audioProcessorVersion++
    if (audioProcessor) {
      const {context, worklet, source} = audioProcessor
      audioProcessor = undefined
      // Drop chunks the worklet already posted, so nothing is queued for sending after close
      worklet.port.onmessage = null
      worklet.disconnect()
      source.disconnect()
      if (context.state !== 'closed') {
        await context.close()
      }
    }
  }

  const createAudioProcessor = async (audioTrack: MediaStreamTrack) => {
    // closeAudioProcessor bumps the version before its first await, so the version read here
    // belongs to this call; any later create or close makes this call give up (latest call wins)
    const closing = closeAudioProcessor()
    const version = audioProcessorVersion
    await closing
    const context = new AudioContext({sampleRate: 48000})
    await context.audioWorklet.addModule('/pcm-worklet.js')
    if (version !== audioProcessorVersion) {
      // Stopped or superseded while the worklet module was loading
      await context.close()
      return
    }
    const stream = new MediaStream([audioTrack])
    const source = context.createMediaStreamSource(stream)
    const worklet = new AudioWorkletNode(context, 'pcm-worklet')
    source.connect(worklet)
    worklet.port.onmessage = ({data}) => {
      enqueueAudioChunk(data)
    }
    audioProcessor = {source, worklet, context}
  }

  const getVideoElement = () => {
    for (const e of document.querySelectorAll('video')) {
      if (e.checkVisibility()) {
        return e
      }
    }
    return null
  }

  const getTextTrack = (videoElement: HTMLVideoElement, label: string) => {
    for (const track of videoElement.textTracks) {
      if (label === track.label) {
        return track
      }
    }
    return null
  }

  const verifyAllowLiveCaption = (event: CustomEvent<{party: string}>) => {
    if (event.detail.party === 'client' || user.party === 'client') {
      liveCaptionStore.allow()
    }
  }

  const handleStartLiveCaption = async () => {
    // start sending binary
    const message = useMessageStore()
    message.addMessage({
      text: t('meeting.notification.liveCaptionStarted'),
    })
    liveCaptionStore.start()
    isLiveCaptionOn = true
    await createAudioProcessor(useStreamStore().micStream?.getAudioTracks()[0])
  }

  const handleStopLiveCaption = async () => {
    isLiveCaptionOn = false
    const message = useMessageStore()
    message.addMessage({
      text: t('meeting.notification.liveCaptionStopped'),
    })
    // Before the await, so an stt:start arriving meanwhile is not overwritten by a late STOP
    liveCaptionStore.stop()
    flushAudioSendBuffer()
    await closeAudioProcessor()
  }

  const handleTranscript = (event: CustomEvent<TranscriptEvent>) => {
    const {party, transcript, isPartial, resultId, partyId, languageCode} = event.detail
    const video = getVideoElement()
    if (!video) {
      return
    }
    let textTrack = getTextTrack(video, partyId)
    if (!textTrack) {
      textTrack = video.addTextTrack('subtitles', partyId, languageCode)
      textTrack.oncuechange = () => cueChangedBus.emit(textTrack!)
    }
    let cues = activeCues.get(partyId)
    if (!cues) {
      cues = new Map()
      activeCues.set(partyId, cues)
    }

    let text = `${party}: ${transcript}`

    // partial update
    if (isPartial) {
      text = text + '...'
      let cue = cues.get(resultId)

      if (!cue) {
        cue = new VTTCue(video.currentTime, video.currentTime + 5, text)
        textTrack.addCue(cue)
        cues.set(resultId, cue)
      } else {
        cue.text = text
        cue.endTime = video.currentTime + 5
      }
      return
    }
    // final transcript
    let cue = cues.get(resultId)

    if (cue) {
      cue.text = text
      cue.endTime = video.currentTime + 3
      cues.delete(resultId)
    } else {
      cue = new VTTCue(video.currentTime, video.currentTime + 3, text)
      textTrack.addCue(cue)
    }
  }

  // A new microphone stream (device switch, unmute, recovery) is captured again
  const stopMicWatch = watch(
    () => useStreamStore().micStream,
    stream => {
      // A microphone switch must not start streaming when live caption is off
      const track = stream?.getAudioTracks()[0]
      if (!isLiveCaptionOn || !track) {
        return
      }
      createAudioProcessor(track)
    }
  )

  onMounted(() => {
    jsonRpc.on('con:ready', verifyAllowLiveCaption)
    jsonRpc.on('stt:start', handleStartLiveCaption)
    jsonRpc.on('stt:stop', handleStopLiveCaption)
    jsonRpc.on('stt:transcript', handleTranscript)
  })

  onUnmounted(() => {
    jsonRpc.off('con:ready', verifyAllowLiveCaption)
    jsonRpc.off('stt:start', handleStartLiveCaption)
    jsonRpc.off('stt:stop', handleStopLiveCaption)
    jsonRpc.off('stt:transcript', handleTranscript)
    stopMicWatch()
    // Without the stt:stop listener nothing could stop the worklet any more
    isLiveCaptionOn = false
    closeAudioProcessor()
  })
}
